import { Button } from 'antd'
import CustomTable from '@/components/TableAntd'
import { Collist } from '@/components/TableAntd/hook'
import { ProjectMaterialPoolDTO } from '@/api/projectPoolApi'
import { formatterPrice } from '@/tools/util'
import Style from '../../style.module.scss'

export const EMPTY_TEXT = '-'

/**
 * 平台状态颜色：1已上架 绿 / 2已下架 灰 / 3审核中 蓝 / 4已驳回 红 / 5已冻结 橙。
 * tag 为卡片 antd Tag 的预设色，className 为表格文字颜色。
 */
export const PLATFORM_STATUS_STYLES: Record<
  string,
  { tag: string; className: string }
> = {
  '1': { tag: 'success', className: Style.statusOnShelf },
  '2': { tag: 'default', className: Style.statusOffShelf },
  '3': { tag: 'processing', className: Style.statusAuditing },
  '4': { tag: 'error', className: Style.statusRejected },
  '5': { tag: 'warning', className: Style.statusFrozen },
}

type Value = string | number | null | undefined

export const textOf = (value: Value) =>
  value === null || value === undefined || value === '' ? EMPTY_TEXT : value

/** 金额展示保留两位小数（仅格式化，不参与计算；复用 SCP 公共 formatterPrice） */
export const moneyOf = (value: Value) =>
  value === null || value === undefined || value === ''
    ? EMPTY_TEXT
    : formatterPrice(value) || EMPTY_TEXT

interface PoolColumn {
  title: string
  width: number
  render?: Collist['render']
}

/** 22 项字段的名称、列宽与取值格式，key 为字段 code（与项目商品池返回字段名一致）；表格与详情抽屉共用 */
export const FIELD_COLUMNS: Record<string, PoolColumn> = {
  xhCode: { title: '咸亨编码', width: 130 },
  customerProductCode: { title: '客户编码（sku/spu）', width: 150 },
  xhTitle: { title: '咸亨标题', width: 220 },
  // 客户标题无值时展示咸亨标题（与卡片标题一致，用户确认）
  customerTitle: {
    title: '客户标题',
    width: 220,
    render: (value: string | null, record: ProjectMaterialPoolDTO) =>
      textOf(value || record.xhTitle),
  },
  brandName: { title: '品牌名称', width: 150 },
  customerCategoryName: { title: '客户类目', width: 150 },
  unitName: { title: '单位名称', width: 90 },
  projectPrice: { title: '项目售价', width: 110, render: moneyOf },
  platformStatus: {
    title: '平台状态',
    width: 90,
    render: (_: string, record: ProjectMaterialPoolDTO) => (
      <span
        className={
          PLATFORM_STATUS_STYLES[record.platformStatus || '']?.className
        }
      >
        {textOf(record.platformStatusName)}
      </span>
    ),
  },
  supplierName: { title: '供应商名称', width: 200 },
  controlPrice: { title: '控价金额', width: 110, render: moneyOf },
  // 厂控：null 为未知，不当作“否”（接口文档）
  factoryControl: {
    title: '厂控物料',
    width: 90,
    render: (value: boolean | null) =>
      value === null || value === undefined ? EMPTY_TEXT : value ? '是' : '否',
  },
  actualSupply: {
    title: '实供物料',
    width: 90,
    render: (value: string | null) =>
      value === '1' ? '是' : value === '0' ? '否' : EMPTY_TEXT,
  },
  remark: { title: '备注', width: 160 },
  standardCode: { title: '标准库编码/产品编码', width: 170 },
  maxPrice: { title: '最高可改价', width: 110, render: moneyOf },
  minPrice: { title: '最低价', width: 110, render: moneyOf },
  salesVolume: { title: '销量', width: 90 },
  areaInventoryList: {
    title: '销售区域&库存',
    width: 200,
    render: (list: ProjectMaterialPoolDTO['areaInventoryList']) =>
      list?.length
        ? list.map((item) => `${item.area}：${item.inventory}`).join('；')
        : EMPTY_TEXT,
  },
  multiplePurchaseQty: { title: '成倍购买量', width: 110 },
  minimumOrderQuantity: { title: '起订量', width: 90 },
  materialCode: { title: '物资编码', width: 150 },
}

/** 按字段 code 取展示值：有 render 的按 render 格式化，否则按文本，空值显示“-” */
export const fieldValueOf = (code: string, record: ProjectMaterialPoolDTO) => {
  const value = record[code as keyof ProjectMaterialPoolDTO]
  const render = FIELD_COLUMNS[code]?.render
  return render ? render(value, record, 0) : textOf(value as Value)
}

/** 可点击打开详情的列（PRD DET-001 BR-01） */
const DETAIL_LINK_FIELDS = ['xhCode', 'customerTitle']

/** 勾选列宽度（TableAntd 内置） */
const SELECTION_WIDTH = 30
interface Props {
  list: ProjectMaterialPoolDTO[]
  fields: string[] // 当前角色可见字段 code，决定展示哪些列
  loading: boolean
  selectedIds: string[] // 与卡片共用的勾选（记录 id），切换展示模式时带入
  onSelect: (ids: string[]) => void
  pagination: {
    total: number
    current: number
    pagesize: number
    onChange: (page: number, pageSize?: number) => void
  }
  onOpenDetail: (xhCode: string) => void
}

/** 项目商品池 - 表格列表（SCP 公共 TableAntd）：按 SCP服务商 字段配置展示列，横向滚动，分页与卡片共用 */
const PoolTable = (props: Props) => {
  const {
    list,
    fields,
    loading,
    selectedIds,
    onSelect,
    pagination,
    onOpenDetail,
  } = props
  const columns: Collist[] = fields
    .filter((code) => FIELD_COLUMNS[code])
    .map((code) => ({
      key: code,
      ...FIELD_COLUMNS[code],
      render: (_: unknown, record: ProjectMaterialPoolDTO) =>
        DETAIL_LINK_FIELDS.includes(code) ? (
          <Button
            type="link"
            size="small"
            className={Style.linkCell}
            onClick={() => onOpenDetail(record.xhCode)}
          >
            {fieldValueOf(code, record)}
          </Button>
        ) : (
          fieldValueOf(code, record)
        ),
    }))
  const scrollX = columns.reduce(
    (sum, column) => sum + Number(column.width || 0),
    SELECTION_WIDTH,
  )

  return (
    <CustomTable
      rowKey="id"
      loading={loading}
      dataSource={list}
      listColumn={columns}
      X={scrollX}
      // 高度撑满父级列表区（页面 flex 布局分配），分页贴底
      selfAdaptScroll
      defaultlist={selectedIds}
      onChangeCheck={(keys: string[]) => onSelect(keys)}
      pagination={pagination}
      showSize
    />
  )
}

export default PoolTable
import { Button } from 'antd'
import CustomTable from '@/components/TableAntd'
import { Collist } from '@/components/TableAntd/hook'
import { ProjectMaterialPoolDTO } from '@/api/projectPoolApi'
import { formatterPrice } from '@/tools/util'
import Style from '../../style.module.scss'

export const EMPTY_TEXT = '-'

/**
 * 平台状态颜色：1已上架 绿 / 2已下架 灰 / 3审核中 蓝 / 4已驳回 红 / 5已冻结 橙。
 * tag 为卡片 antd Tag 的预设色，className 为表格文字颜色。
 */
export const PLATFORM_STATUS_STYLES: Record<
  string,
  { tag: string; className: string }
> = {
  '1': { tag: 'success', className: Style.statusOnShelf },
  '2': { tag: 'default', className: Style.statusOffShelf },
  '3': { tag: 'processing', className: Style.statusAuditing },
  '4': { tag: 'error', className: Style.statusRejected },
  '5': { tag: 'warning', className: Style.statusFrozen },
}

type Value = string | number | null | undefined

export const textOf = (value: Value) =>
  value === null || value === undefined || value === '' ? EMPTY_TEXT : value

/** 金额展示保留两位小数（仅格式化，不参与计算；复用 SCP 公共 formatterPrice） */
export const moneyOf = (value: Value) =>
  value === null || value === undefined || value === ''
    ? EMPTY_TEXT
    : formatterPrice(value) || EMPTY_TEXT

interface PoolColumn {
  title: string
  width: number
  render?: Collist['render']
}

/** 22 项字段的名称、列宽与取值格式，key 为字段 code（与项目商品池返回字段名一致）；表格与详情抽屉共用 */
export const FIELD_COLUMNS: Record<string, PoolColumn> = {
  xhCode: { title: '咸亨编码', width: 130 },
  customerProductCode: { title: '客户编码（sku/spu）', width: 150 },
  xhTitle: { title: '咸亨标题', width: 220 },
  // 客户标题无值时展示咸亨标题（与卡片标题一致，用户确认）
  customerTitle: {
    title: '客户标题',
    width: 220,
    render: (value: string | null, record: ProjectMaterialPoolDTO) =>
      textOf(value || record.xhTitle),
  },
  brandName: { title: '品牌名称', width: 150 },
  customerCategoryName: { title: '客户类目', width: 150 },
  unitName: { title: '单位名称', width: 90 },
  projectPrice: { title: '项目售价', width: 110, render: moneyOf },
  platformStatus: {
    title: '平台状态',
    width: 90,
    render: (_: string, record: ProjectMaterialPoolDTO) => (
      <span
        className={
          PLATFORM_STATUS_STYLES[record.platformStatus || '']?.className
        }
      >
        {textOf(record.platformStatusName)}
      </span>
    ),
  },
  supplierName: { title: '供应商名称', width: 200 },
  controlPrice: { title: '控价金额', width: 110, render: moneyOf },
  // 厂控：null 为未知，不当作“否”（接口文档）
  factoryControl: {
    title: '厂控物料',
    width: 90,
    render: (value: boolean | null) =>
      value === null || value === undefined ? EMPTY_TEXT : value ? '是' : '否',
  },
  actualSupply: {
    title: '实供物料',
    width: 90,
    render: (value: string | null) =>
      value === '1' ? '是' : value === '0' ? '否' : EMPTY_TEXT,
  },
  remark: { title: '备注', width: 160 },
  standardCode: { title: '标准库编码/产品编码', width: 170 },
  maxPrice: { title: '最高可改价', width: 110, render: moneyOf },
  minPrice: { title: '最低价', width: 110, render: moneyOf },
  salesVolume: { title: '销量', width: 90 },
  areaInventoryList: {
    title: '销售区域&库存',
    width: 200,
    render: (list: ProjectMaterialPoolDTO['areaInventoryList']) =>
      list?.length
        ? list.map((item) => `${item.area}：${item.inventory}`).join('；')
        : EMPTY_TEXT,
  },
  multiplePurchaseQty: { title: '成倍购买量', width: 110 },
  minimumOrderQuantity: { title: '起订量', width: 90 },
  materialCode: { title: '物资编码', width: 150 },
}

/** 按字段 code 取展示值：有 render 的按 render 格式化，否则按文本，空值显示“-” */
export const fieldValueOf = (code: string, record: ProjectMaterialPoolDTO) => {
  const value = record[code as keyof ProjectMaterialPoolDTO]
  const render = FIELD_COLUMNS[code]?.render
  return render ? render(value, record, 0) : textOf(value as Value)
}

/** 可点击打开详情的列（PRD DET-001 BR-01） */
const DETAIL_LINK_FIELDS = ['xhCode', 'customerTitle']

/** 勾选列宽度（TableAntd 内置） */
const SELECTION_WIDTH = 30
interface Props {
  list: ProjectMaterialPoolDTO[]
  fields: string[] // 当前角色可见字段 code，决定展示哪些列
  loading: boolean
  selectedIds: string[] // 与卡片共用的勾选（记录 id），切换展示模式时带入
  onSelect: (ids: string[]) => void
  pagination: {
    total: number
    current: number
    pagesize: number
    onChange: (page: number, pageSize?: number) => void
  }
  onOpenDetail: (xhCode: string) => void
}

/** 项目商品池 - 表格列表（SCP 公共 TableAntd）：按 SCP服务商 字段配置展示列，横向滚动，分页与卡片共用 */
const PoolTable = (props: Props) => {
  const {
    list,
    fields,
    loading,
    selectedIds,
    onSelect,
    pagination,
    onOpenDetail,
  } = props
  const columns: Collist[] = fields
    .filter((code) => FIELD_COLUMNS[code])
    .map((code) => ({
      key: code,
      ...FIELD_COLUMNS[code],
      render: (_: unknown, record: ProjectMaterialPoolDTO) =>
        DETAIL_LINK_FIELDS.includes(code) ? (
          <Button
            type="link"
            size="small"
            className={Style.linkCell}
            onClick={() => onOpenDetail(record.xhCode)}
          >
            {fieldValueOf(code, record)}
          </Button>
        ) : (
          fieldValueOf(code, record)
        ),
    }))
  const scrollX = columns.reduce(
    (sum, column) => sum + Number(column.width || 0),
    SELECTION_WIDTH,
  )

  return (
    <CustomTable
      rowKey="id"
      loading={loading}
      dataSource={list}
      listColumn={columns}
      X={scrollX}
      // 高度撑满父级列表区（页面 flex 布局分配），分页贴底
      selfAdaptScroll
      defaultlist={selectedIds}
      onChangeCheck={(keys: string[]) => onSelect(keys)}
      pagination={pagination}
      showSize
    />
  )
}

export default PoolTable
